import type {
  FhssBenefitOutput,
  FhssEligibilityOutput,
  FhssGateResult,
  FhssRuleSet,
} from "./types.js";

// Plain-English FHSS copy. Lives in the rules package so every surface (Expo
// result screen, marketing explainer, PDF) renders identical wording from one
// source. This is a REGULATORY requirement, not just DRY: FHSS copy is
// INFORMATION, not advice (validation §3, spec §4). We STATE the gates, the
// releasable figure (NET of the 85% haircut + SG exclusion), and the rate math —
// with NO "you should use FHSS", NO "put money in super", NO product
// recommendation, NO "save $X".
//
// FHSS-specific guardrails (panel ship-conditions, binding):
//  - The endpoint is an ATO determination request — a GOVERNMENT process, not a
//    product sale. There is NO super-fund / mortgage / credit handoff anywhere
//    in this copy (the C2 AFSL kill-line). $0 affiliate by design.
//  - Surface the one-shot gates + 12-month clock BEFORE the number; show the
//    releasable figure net of the 85% haircut. Overstating the entitlement is
//    the ACL misleading-conduct exposure.
//  - Deemed earnings (SIC) are stated as "added on release", never computed.
//  - The benefit comparison presents the rate math as FACTS, never a "you'd be
//    better off / you should contribute" recommendation — this tool sits at a
//    "put money in super for a house?" decision and must not make it.

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** A rate as a clean percentage, e.g. 0.32 → "32%". */
function ratePct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

// --- Surface 1: eligibility + releasable ------------------------------------

const GATE_FAIL_REASON: Record<FhssGateResult["gate"], string> = {
  age: "FHSS requires you to be 18 or older when you request a determination.",
  firstHome:
    "FHSS is for buying or building your first home to live in — not an investment property.",
  neverOwned:
    "FHSS generally requires that you have never owned property in Australia (including investment or commercial property, vacant land, or a lease of land), unless a financial-hardship exception applies.",
  neverReleased:
    "FHSS is one-shot — it is not available if you have already made a valid FHSS release request.",
};

/** Headline eligibility verdict — qualitative, never "you're owed". */
export function eligibilityHeadline(out: FhssEligibilityOutput): string {
  return out.eligible
    ? "On these answers you appear to meet the FHSS eligibility gates."
    : "On these answers you do not appear to meet all of the FHSS eligibility gates.";
}

/** The specific gate(s) that failed — surfaced before any dollar figure. */
export function gateBullets(out: FhssEligibilityOutput): string[] {
  return out.gates
    .filter((g) => !g.pass)
    .map((g) => GATE_FAIL_REASON[g.gate]);
}

/** States the maximum RELEASABLE figure — net of the 85% haircut, with the
 * earnings + caps caveats. Never the naive contributions sum. */
export function releasableBullet(out: FhssEligibilityOutput): string {
  if (out.maxReleasableContributions === 0) {
    return "On these contributions there is nothing releasable under FHSS yet. Only voluntary contributions count — your employer's compulsory super (Super Guarantee) is never eligible.";
  }
  return `Based on this year's eligible voluntary contributions, the most you could release under FHSS is ${formatAud(
    out.maxReleasableContributions,
  )} of contributions. This is net of the rule that only 85% of concessional (before-tax) contributions count toward the release; non-concessional (after-tax) contributions count in full. The ATO adds deemed associated earnings on top of this at the time of release.`;
}

/** Caps + SG-exclusion facts. */
export function capsBullet(
  out: FhssEligibilityOutput,
  rules: FhssRuleSet,
): string {
  const parts = [
    `FHSS counts up to ${formatAud(rules.annualEligibleCap)} of eligible voluntary contributions in any one financial year, and up to ${formatAud(
      rules.lifetimeEligibleCap,
    )} across all years.`,
  ];
  if (out.overAnnualCap > 0) {
    parts.push(
      `${formatAud(out.overAnnualCap)} of this year's contributions is above the ${formatAud(
        rules.annualEligibleCap,
      )} annual cap and does not count toward FHSS.`,
    );
  }
  parts.push(
    `You have ${formatAud(out.lifetimeHeadroom)} of lifetime FHSS counting room remaining.`,
  );
  return parts.join(" ");
}

/** The 12-month (max 24) contract clock — a fact, surfaced with the gates. */
export function timingBullet(rules: FhssRuleSet): string {
  return `After you request a release, you generally have ${rules.contractWindowMonths} months to sign a contract to buy or build, which the ATO can extend by a further ${rules.contractWindowMonths} months (up to ${rules.contractWindowMaxMonths} months in total).`;
}

/** Source-attribution line. */
export function eligibilitySourceBullet(rules: FhssRuleSet): string {
  return `These figures use the ATO First Home Super Saver scheme rules for ${rules.fy} (last verified ${rules.sourcesVerifiedAt}). FHSS is a government scheme — the release is requested from the ATO directly, not from your superannuation provider or a lender. Always check ato.gov.au.`;
}

/** Mandatory eligibility disclaimer (information, not advice). */
export const ELIGIBILITY_DISCLAIMER =
  "Educational tool. Not financial advice. This is a check against the published FHSS gates and caps, not an ATO determination, and not a recommendation to use the scheme. The ATO assesses your actual eligibility and releasable amount. Confirm at ato.gov.au.";

export function eligibilityExplanation(
  out: FhssEligibilityOutput,
  rules: FhssRuleSet,
): string[] {
  return [
    eligibilityHeadline(out),
    ...gateBullets(out),
    timingBullet(rules),
    releasableBullet(out),
    capsBullet(out, rules),
    eligibilitySourceBullet(rules),
    ELIGIBILITY_DISCLAIMER,
  ];
}

// --- Surface 2: benefit vs bank ---------------------------------------------

/** Headline — states the tax difference as a FACT, never a recommendation. */
export function benefitHeadline(out: FhssBenefitOutput): string {
  if (out.lowIncomeErosion) {
    return "At this income, saving through FHSS does not produce a tax benefit over a bank account — the 15% tax on money going into super is at or above your marginal tax rate.";
  }
  if (out.contributionsTaxBenefit <= 0) {
    return "At this income, the FHSS tax treatment and a bank account work out about the same on the contributions.";
  }
  return `On these figures, ${formatAud(
    out.grossContribution,
  )} saved through FHSS keeps ${formatAud(
    out.contributionsTaxBenefit,
  )} more of the contributions after tax than the same amount saved in a bank account — a consequence of the tax rules, stated here as a fact, not a recommendation.`;
}

/** Spells out the in/out rate math behind the difference. */
export function rateMathBullet(out: FhssBenefitOutput): string {
  return `Through FHSS, before-tax money is taxed at ${ratePct(
    0.15,
  )} going into super, then on release is taxed at your marginal rate plus Medicare levy (${ratePct(
    out.effectiveMarginalRate,
  )}) less a 30% offset — a release tax of ${ratePct(
    out.releaseTaxRate,
  )} here. In a bank account, the same money is taxed once at your marginal rate plus Medicare levy (${ratePct(
    out.effectiveMarginalRate,
  )}).`;
}

/** Deemed-earnings honesty caveat. */
export const EARNINGS_CAVEAT =
  "FHSS adds deemed earnings on release at the ATO's set rate (the Shortfall Interest Charge rate), not your fund's actual investment return — so your real earnings inside super may be higher or lower than the amount released.";

/** Low-income erosion honesty caveat. */
export const LOW_INCOME_CAVEAT =
  "If your marginal tax rate is at or below the 15% tax that applies to before-tax super contributions, FHSS can leave you with less than a bank account on the contributions — the scheme favours higher marginal rates.";

/** Source-attribution line for the benefit comparison. */
export function benefitSourceBullet(rules: FhssRuleSet): string {
  return `This comparison uses the ATO FHSS rules for ${rules.fy} (last verified ${rules.sourcesVerifiedAt}): 15% contributions tax in, and a release taxed at your marginal rate (including Medicare levy) less a 30% offset. Always check ato.gov.au.`;
}

/** Mandatory benefit disclaimer (information, not advice). */
export const BENEFIT_DISCLAIMER =
  "Educational tool. Not financial advice. This compares the tax treatment of the contributions only — it is not advice to put money into super, and not a recommendation about your home deposit. Whether FHSS suits you depends on your circumstances. Confirm the rules at ato.gov.au.";

export function benefitExplanation(
  out: FhssBenefitOutput,
  rules: FhssRuleSet,
): string[] {
  const bullets = [benefitHeadline(out), rateMathBullet(out), EARNINGS_CAVEAT];
  if (out.lowIncomeErosion) bullets.push(LOW_INCOME_CAVEAT);
  bullets.push(benefitSourceBullet(rules), BENEFIT_DISCLAIMER);
  return bullets;
}
